import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'react-hot-toast';
import { TextField, Button, CircularProgress, Container, Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import passwordService from '../services/PasswordService';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: theme.palette.background.default
  },
  card: {
    width: '100%',
    maxWidth: 400,
    padding: theme.spacing(4),
    background: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2],
    textAlign: 'center'
  },
  title: {
    marginBottom: theme.spacing(1),
    fontWeight: 600
  },
  description: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: '0.95rem'
  },
  form: {
    width: '100%'
  },
  textField: {
    marginBottom: theme.spacing(2)
  },
  button: {
    marginTop: theme.spacing(2),
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 500
  },
  successMessage: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    background: theme.palette.success.light,
    color: theme.palette.success.dark,
    borderRadius: theme.spacing(0.5),
    fontSize: '0.9rem'
  }
}));

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address')
});

const ForgotPasswordForm = () => {
  const classes = useStyles();
  const [isSubmitting, setSubmitting] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
    mode: 'onChange'
  });

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);

      await passwordService.createPasswordResetToken(data?.email);

      toast.success('Reset link sent! Check your email.');

      setIsSuccessful(true);
      reset();
    } catch (error) {
      toast.error('Unable to send reset link. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className={classes.container}>
      <Container maxWidth="sm">
        <Box className={classes.card}>
          <Typography variant="h6" className={classes.title}>
            Reset Your Password
          </Typography>
          <Typography className={classes.description}>
            Enter your email address and we&apos;ll send you a link to reset your password.
          </Typography>

          {!isSuccessful ? (
            <form onSubmit={handleSubmit(onSubmit)} className={classes.form} noValidate>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    type="email"
                    fullWidth
                    variant="outlined"
                    placeholder="your@email.com"
                    className={classes.textField}
                    error={!!errors.email}
                    helperText={errors.email?.message || ''}
                    disabled={isSubmitting}
                  />
                )}
              />
              {errors.email && <p className="text-sm text-red-500 ">{errors.email.message}</p>}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.button}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={20} style={{ marginRight: '8px' }} />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>
          ) : (
            <Box className={classes.successMessage}>
              <Typography>
                If this email exists in our system, you&apos;ll receive a password reset link shortly.
              </Typography>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPasswordForm;
